import { useCallback, useEffect, useRef, useState, type ClipboardEvent } from "react";
import Form from "react-bootstrap/Form";
import Card from "react-bootstrap/Card";
import Col from "react-bootstrap/Col";
import Row from "react-bootstrap/Row";
import { useTranslation } from "react-i18next";
import Button from "react-bootstrap/Button";
import { Question } from "react-bootstrap-icons";
import { RegisterCong } from "../../types/register/registerCong";
import { getLocaleWithCode } from "../../helpers/locale";
import { OverlayTrigger, Popover } from "react-bootstrap";
import HourglassGlobals from "../../helpers/globals";
import { getGUID, mergePastedShareURL } from "../../helpers/util";

export function meetingSearchWindow() {
  window.open("https://hub.jw.org/meetings", "_blank");
}

export function congShareHelp() {
  window.open("https://youtu.be/nT0gV8t4rJo", "_blank");
}

function CongInfo(props: {
  cong: RegisterCong;
  onCongChange: (name: keyof RegisterCong, value: any) => void;
  formValidated: boolean;
}) {
  const { t, i18n } = useTranslation();
  const { cong, onCongChange } = props;
  const [triedFindCong, setTriedFindCong] = useState(false);
  const [haveGuid, setHaveGuid] = useState(false);
  const applyShareText = (field: HTMLInputElement | HTMLTextAreaElement, value: string) => {
    //verify that we have valid share text: look for the guid
    if (value) {
      if (!getGUID(value)) {
        field.setCustomValidity(t("popup.error.title.invalid-input"));
        setHaveGuid(false);
      } else {
        field.setCustomValidity("");
        setHaveGuid(true);
      }
    }

    onCongChange(field.name as keyof RegisterCong, value);
  };

  const handleShareChange = (e: any) => {
    applyShareText(e.currentTarget, e.currentTarget.value);
  };

  const handleSharePaste = (e: ClipboardEvent<HTMLTextAreaElement>) => {
    const merged = mergePastedShareURL(e.clipboardData);
    if (!merged) return;
    e.preventDefault();
    const field = e.currentTarget;
    const current = field.value;
    const start = field.selectionStart ?? current.length;
    const end = field.selectionEnd ?? current.length;
    applyShareText(field, current.slice(0, start) + merged + current.slice(end));
  };

  const handleCheckChange = (e: any) => {
    onCongChange(e.currentTarget.name, e.currentTarget.checked);
  };

  const handleNumberChange = (e: any) => {
    const numberValue = parseInt(e.currentTarget.value);
    if (isNaN(numberValue) || numberValue < 1) return;
    onCongChange(e.currentTarget.name, numberValue);
  };

  const timezonesForCountry = useCallback((): Timezone[] => {
    const selectedCountry =
      window.HGGlobal?.localeData.countries.find((country) => country.id === cong.countriesId) ??
      HourglassGlobals.countries()[0]!;
    if (!cong.countriesId || !selectedCountry) {
      return [];
    }
    const countryTimezones =
      window.HGGlobal?.localeData.timezones.filter((tz) => tz.countrycode === selectedCountry.code) ??
      HourglassGlobals.timezones();
    if (countryTimezones.length > 1) {
      countryTimezones.sort((a, b) => {
        if (a.offset > b.offset) return 1;
        if (b.offset > a.offset) return -1;
        return 0;
      });
      countryTimezones.unshift({ id: 0, name: "", offset: "", countrycode: selectedCountry.code });
    }
    return countryTimezones;
  }, [cong.countriesId]);

  const countries = useRef<Country[]>(new Array<Country>());
  const languages = useRef<Locale[]>(new Array<Locale>());

  const curLocale = getLocaleWithCode(i18n.language);
  // tracks the curLocale.id we last auto-applied, so this only re-applies when the display
  // language actually changes, not whenever cong.localesId changes for some other reason
  // (e.g. the user manually picking a different value in the language select)
  const appliedLocaleId = useRef<number | null>(null);
  const selectLanguage = useCallback(() => {
    if (appliedLocaleId.current === curLocale.id) return;
    appliedLocaleId.current = curLocale.id;
    onCongChange("localesId", curLocale.id);
  }, [onCongChange, curLocale]);

  useEffect(() => {
    const tzs = timezonesForCountry();
    // guard against re-selecting: without this check, this effect would re-fire and update
    // parent state forever, since onCongChange isn't memoized by the parent
    // @ts-ignore noUncheckedIndexedAccess
    if (tzs.length === 1 && tzs[0].id !== cong.timezonesId) {
      //if there's only one time zone, select it
      // @ts-ignore noUncheckedIndexedAccess
      onCongChange("timezonesId", tzs[0].id);
    }
  }, [cong.timezonesId, onCongChange, timezonesForCountry]);

  //one-time sort of the countries & languages
  useEffect(() => {
    const coll = new Intl.Collator(i18n.language);
    countries.current =
      window.HGGlobal?.localeData.countries.sort((a, b) => {
        return coll.compare(a.name, b.name);
      }) ?? HourglassGlobals.countries();
    languages.current =
      window.HGGlobal?.localeData.locales.sort((a, b) => {
        return coll.compare(a.name, b.name);
      }) ?? HourglassGlobals.locales();

    //select the current language
    selectLanguage();
    // i18n.language is intentionally included: curLocale is derived from it, and we want this to
    // re-run (and re-select the language) whenever the display language changes.
  }, [curLocale, selectLanguage, i18n.language]);

  return (
    <Card>
      <Card.Header>
        <h5>{t("list.congregation.title")}</h5>
      </Card.Header>

      <Card.Body className="hg-user-edit">
        <Row>
          <Col md={4}>
            <Form.Group controlId="number">
              <Form.Label>{t("register.cong-number")}</Form.Label>
              <Form.Control
                className="mt-1"
                required
                name="number"
                type="number"
                min="1"
                max="9999999"
                value={cong.number || ""}
                autoComplete="off"
                isInvalid={cong.congNumInvalid}
                onChange={handleNumberChange}
              />
            </Form.Group>
          </Col>
          <Col lg={8} sm={12}>
            <Form.Group controlId="shareText">
              <Form.Label>
                {t("register.cong-share")}
                <Button
                  className="ms-2"
                  variant="info"
                  size="sm"
                  aria-label={t("nav.help")}
                  onClick={() => {
                    setTriedFindCong(true);
                    congShareHelp();
                  }}
                >
                  <Question color="white" size={16} />
                </Button>
                <Button
                  className="ms-2"
                  size="sm"
                  variant="primary"
                  onClick={() => {
                    setTriedFindCong(true);
                    meetingSearchWindow();
                  }}
                >
                  {t("search.hint")}
                </Button>
                {triedFindCong && !haveGuid && (
                  <OverlayTrigger
                    trigger="click"
                    placement="bottom"
                    overlay={
                      <Popover>
                        <Popover.Body>{t("register.cong-not-listed.help")}</Popover.Body>
                      </Popover>
                    }
                  >
                    <Button className="ms-2" size="sm" variant="secondary">
                      {t("register.cong-not-listed.title")}
                    </Button>
                  </OverlayTrigger>
                )}
              </Form.Label>
              <Form.Control
                required
                as="textarea"
                name="shareText"
                rows={6}
                value={cong.shareText}
                isInvalid={cong.shareTextInvalid}
                onChange={handleShareChange}
                onPaste={handleSharePaste}
              />
            </Form.Group>
          </Col>
        </Row>
        <Row className="mt-4">
          <Col sm={6} md={4}>
            <Form.Group controlId="countryId">
              <Form.Label>{t("congprofile.country")}</Form.Label>
              <Form.Select required name="countriesId" value={cong.countriesId} onChange={handleNumberChange}>
                <option key={0} value="" />
                {countries.current.map((country) => (
                  <option key={country.id} value={country.id}>
                    {country.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
          <Col sm={6} md={4}>
            <Form.Group controlId="timezoneId">
              <Form.Label>{t("congprofile.time-zone")}</Form.Label>
              <Form.Select required name="timezonesId" value={cong.timezonesId} onChange={handleNumberChange}>
                {timezonesForCountry().map((tz) => (
                  <option key={tz.id} value={tz.id > 0 ? tz.id : ""}>
                    {tz.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
          <Col sm={6} md={4}>
            <Form.Group controlId="localeId">
              <Form.Label>{t("congprofile.language")}</Form.Label>
              <Form.Select required name="localesId" value={cong.localesId} onChange={handleNumberChange}>
                <option key={0} value="" />
                {languages.current.map((lc) => (
                  <option key={lc.id} value={lc.id}>
                    {lc.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
        </Row>
        <Row className="mt-4">
          <Col>
            <Form.Check
              id="check-confirm-svc-cmte"
              required
              type="checkbox"
              name="approved"
              label={
                <span>
                  {t("register.confirm.service-committee")}{" "}
                  <a href="https://www.hourglass-app.com/en/tos/" target="_blank" rel="noreferrer">
                    {t("register.confirm.tos")}
                  </a>
                </span>
              }
              checked={cong.approved}
              onChange={handleCheckChange}
            />
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );
}

export default CongInfo;
